import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import SnackbarInfo from '../common/snackbar-info.component';
import PlaylistLevels from '../form-elements/playlist-level.component';
import Playlists from '../form-elements/playlists.component';
import VideoOrder from '../form-elements/video-order.component';
import ChannelGenres from '../form-elements/channel-genres.component';
import Channels from '../form-elements/channels.component';
import { getChannelListByGenreId } from '../../services/channel-genres.service';
import { getSearchListData } from '../../services/apps.service';
import { VIDEO_ORDER_FIELDS } from '../../config/const.config';
import {
  updateDataById,
  createNewData,
} from '../../services/playlists.service';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';
import ConfirmPopup from '../common/confirm-popup.component';

const PlaylistSettings = ({
  playlistDetail,
  handleRefreshData,
  formValueChangedCallback,
  editMode,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: '',
      uniqueName: '',
      channels: [],
      description: null,
      playlistLevel: 1,
      parentPlaylist: { id: 0, name: 'No Parent' },
      image: null,
      globalPlaylist: false,
      playlistEnable: false,
      checkChannelVideoAssignment: true,
      checkYouTubePlaylist: false,
      videoOrder: 'title_name_only',
      videoSortOrder: 'asc',
      minimumVideos: 0,
      maximumVideos: 0,
      channelGenres: [],
      status: 1,
    },
  });

  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingOnChange, setPendingOnChange] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const watchChannelGenres = watch('channelGenres');

  const handleAddAllChannel = () => {
    getSearchListData()
      .then((res) => {
        setValue('channels', res.data);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const saveNewData = (payload) => {
    createNewData(payload)
      .then((res) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'New Playlist created successfully.',
        });
        formValueChangedCallback(false);
        setFormSubmitted(false);
        const navigateUrl = replaceIdUrl(
          ROUTE_PATH.CONTENT_ALL_PLAYLISTS_EDIT,
          res.data.id
        );
        navigate(navigateUrl);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(playlistDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Playlist details updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (
      Number(data.minimumVideos) &&
      Number(data.maximumVideos) &&
      Number(data.minimumVideos) > Number(data.maximumVideos)
    ) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            'Invalid Min and Max Number of Titles value. Max Number of Titles must be greater than Min Number of Titles.',
        });
      }, 50);
      setFormSubmitted(false);
      return;
    }

    const payload = {
      name: data.name,
      uniqueName: data?.uniqueName,
      channels: data.channels.map((channel) => ({
        id: channel.id,
        title: channel.title,
        code: channel.code,
      })),
      description: data.description,
      playlistLevel: data.playlistLevel,
      parentPlaylist:
        data.playlistLevel === 1 ? data.parentPlaylist?.id || null : null,
      image:
        data.playlistLevel === 1 && data.parentPlaylist?.id ? data.image : null,
      globalPlaylist: data.globalPlaylist,
      checkChannelVideoAssignment:
        data.playlistLevel === 1 ? data.checkChannelVideoAssignment : true,
      checkYouTubePlaylist:
        data.playlistLevel === 1 ? data.checkYouTubePlaylist : true,
      playlistEnable: data.playlistEnable,
      videoOrder: data.videoOrder,
      videoSortOrder: data.videoSortOrder,
      minimumVideos: Number(data.minimumVideos) || 0,
      maximumVideos: Number(data.maximumVideos) || 0,
      channelGenres: data.channelGenres.map((item) => item.id),
      status: data.status,
    };
    if (data.checkYouTubePlaylist) {
      payload.checkChannelVideoAssignment = false;
    }
    if (editMode) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!watchChannelGenres.length) return;

    const genreId =
      watchChannelGenres[watchChannelGenres.length - 1]?.id || null;
    if (!genreId) return;

    getChannelListByGenreId(genreId)
      .then((res) => {
        const watchChannels = watch('channels');
        const additionalChannels = [];

        res.data.forEach((c) => {
          if (!watchChannels.some((item) => item.id === c.id)) {
            additionalChannels.push(c);
          }
        });
        setValue('channels', [...watchChannels, ...additionalChannels]);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  }, [watchChannelGenres]);

  useEffect(() => {
    if (!editMode) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    const channels = playlistDetail.channels.map((channel) => ({
      id: channel.id || null,
      title: channel.name || '',
      code: channel.code || '',
    }));
    const parentPlaylist = playlistDetail.parentId
      ? {
          id: playlistDetail.parentId,
          name: playlistDetail.parentPlaylistName,
          uniqueName: playlistDetail?.parentPlaylistUniqueName || '',
        }
      : { id: 0, name: 'No Parent' } || { id: 0, name: 'No Parent' };
    const videoOrder = playlistDetail.videoSort?.startsWith('-')
      ? playlistDetail.videoSort?.substring(1)
      : playlistDetail.videoSort || 'title_name_only';

    setValue('name', playlistDetail.name || '');
    setValue('uniqueName', playlistDetail?.uniqueName || '');
    setValue('channels', channels);
    setValue('description', playlistDetail.description || null);
    setValue('playlistLevel', playlistDetail.level || null);
    setValue('parentPlaylist', parentPlaylist);
    setValue('image', playlistDetail.image || null);
    setValue('globalPlaylist', !!playlistDetail.globalPlaylist || false);
    if (playlistDetail.checkYouTubePlaylist || watch('checkYouTubePlaylist')) {
      setValue('checkChannelVideoAssignment', false);
    } else {
      setValue(
        'checkChannelVideoAssignment',
        !!playlistDetail.checkChannelVideoAssignment || false
      );
    }
    setValue('playlistEnable', !!playlistDetail.live || false);
    setValue('videoOrder', videoOrder);
    setValue(
      'videoSortOrder',
      playlistDetail.videoSort.startsWith('-') ? 'desc' : 'asc'
    );
    setValue('minimumVideos', playlistDetail.minimumVideos || 0);
    setValue('maximumVideos', playlistDetail.maximumVideos || 0);
    setValue('channelGenres', playlistDetail.channelGenres);
    setValue(
      'checkYouTubePlaylist',
      !!playlistDetail.checkYouTubePlaylist || false
    );
    setIsLoading(false);
  }, [playlistDetail.id]);

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      {!isLoading && (
        <>
          <form
            id="playlist-settings-form"
            onSubmit={handleSubmit(onFormSubmit)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(128),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Display Name *"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="uniqueName"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(128),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name *"
                      type="text"
                      error={!!errors.uniqueName}
                      helperText={errors?.uniqueName?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description "
                      type="text"
                      multiline
                      minRows={5}
                      maxRows={5}
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="playlistLevel"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <PlaylistLevels
                      id="playlist_level"
                      name="playlist_level"
                      label="Playlist level"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[1, 2]}
                      sx={{ width: '100%' }}
                      error={errors?.playlistLevel?.message || ''}
                    />
                  )}
                />
              </Grid>

              {watch('playlistLevel') === 1 && (
                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="parentPlaylist"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Playlists
                        id="parent_playlist"
                        name="parent_playlist"
                        label="Select parent playlist "
                        defaultValue={value}
                        multiple={false}
                        onChange={onChange}
                        sx={{ width: '100%' }}
                        parentPlaylist
                      />
                    )}
                  />
                </Grid>
              )}

              {watch('playlistLevel') === 1 &&
                !!watch('parentPlaylist')?.id && (
                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="image"
                      control={control}
                      rules={{
                        ...validationRules.OPTIONAL_URL,
                        ...maxLengthValidation(128),
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Playlist Image"
                          type="text"
                          error={!!errors.image}
                          helperText={errors?.image?.message || null}
                          fullWidth
                          variant="filled"
                        />
                      )}
                    />
                  </Grid>
                )}

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="globalPlaylist"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={onChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                          color="primary"
                        />
                      }
                      label="Global Playlist"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="playlistEnable"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={onChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                          color="primary"
                        />
                      }
                      label="Playlist Enabled"
                    />
                  )}
                />
              </Grid>

              {watch('playlistLevel') === 1 && (
                <>
                  <Grid item xs={6} sm={6} md={6}>
                    <Controller
                      name="checkChannelVideoAssignment"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value}
                              disabled={watch('checkYouTubePlaylist')}
                              onChange={(e) => {
                                const { checked } = e.target;

                                // ON -> OFF
                                if (value && !checked) {
                                  setPendingOnChange(() => onChange);
                                  setShowConfirmPopup(true);
                                  return;
                                }

                                // OFF -> ON
                                onChange(checked);
                              }}
                              inputProps={{ 'aria-label': 'controlled' }}
                              color="primary"
                            />
                          }
                          label="Only show videos assigned to this channel"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <Controller
                      name="checkYouTubePlaylist"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value}
                              onChange={(e) => {
                                const { checked } = e.target;
                                onChange(checked);

                                if (checked) {
                                  setValue(
                                    'checkChannelVideoAssignment',
                                    false
                                  );
                                }
                              }}
                              inputProps={{ 'aria-label': 'controlled' }}
                              color="primary"
                            />
                          }
                          label="YouTube Playlist"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="videoOrder"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <VideoOrder
                      id="video_order"
                      name="video_order"
                      label="Video Order"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={Object.keys(VIDEO_ORDER_FIELDS)}
                      sx={{ width: '100%' }}
                      error={errors?.videoOrder?.message || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="videoSortOrder"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <p
                        style={{
                          marginBottom: '4px',
                          marginTop: 0,
                          fontWeight: 'bold',
                        }}
                      >
                        Select video sorting order
                      </p>
                      <RadioGroup
                        {...field}
                        style={{ flexDirection: 'row' }}
                        aria-label="type"
                        name="type"
                      >
                        <FormControlLabel
                          value="asc"
                          control={<Radio />}
                          label="Ascending"
                        />
                        <FormControlLabel
                          value="desc"
                          control={<Radio />}
                          label="Descending"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              {watch('playlistLevel') === 1 && (
                <>
                  <Grid item xs={12} sm={6} md={6}>
                    <Controller
                      name="minimumVideos"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Min Number of Titles"
                          type="number"
                          error={!!errors.minimumVideos}
                          helperText={errors?.minimumVideos?.message || null}
                          fullWidth
                          variant="filled"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Controller
                      name="maximumVideos"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Max Number of Titles"
                          type="number"
                          error={!!errors.maximumVideos}
                          helperText={errors?.maximumVideos?.message || null}
                          fullWidth
                          variant="filled"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              {(!watch('parentPlaylist')?.id ||
                watch('playlistLevel') === 2) && (
                <>
                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="channelGenres"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <ChannelGenres
                          id="channel_genres"
                          name="channel_genres"
                          label="Select Channel Genres"
                          defaultValue={value}
                          multiple
                          onChange={onChange}
                          sx={{ width: '100%' }}
                          hideNoneOption
                          error={errors?.channelGenres?.message || ''}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="channels"
                      control={control}
                      rules={validationRules.REQUIRED}
                      render={({ field: { onChange, value } }) => (
                        <Channels
                          id="playlist_channels"
                          name="playlist_channels"
                          label="Select Channels *"
                          defaultValue={value}
                          multiple
                          onChange={(channel) => {
                            onChange(channel);
                          }}
                          sx={{ width: '100%' }}
                          error={errors?.channels?.message || ''}
                        />
                      )}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    display="flex"
                    justifyContent="center"
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleAddAllChannel}
                      sx={{ ml: 1 }}
                    >
                      Select All Channels
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </form>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2.5 }}
          >
            <Button
              type="submit"
              form="playlist-settings-form"
              color="primary"
              variant="contained"
              disabled={formSubmitted}
            >
              Save Playlist Settings
            </Button>
            <Typography variant="body2" color="secondary">
              * indicates a required field
            </Typography>
          </Stack>
        </>
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          title="Disable channel video assignment filter"
          message="Unlinking this playlist will include videos not assigned to target channels. Are you sure you want to proceed?"
          onCancel={() => {
            setShowConfirmPopup(false);
            setPendingOnChange(null);
          }}
          onSuccess={() => {
            pendingOnChange?.(false);
            setShowConfirmPopup(false);
            setPendingOnChange(null);
          }}
          successButtonText="Yes"
          cancelButtonText="Cancel"
        />
      )}
      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

PlaylistSettings.propTypes = {
  playlistDetail: PropTypes.object.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default PlaylistSettings;
